import { Button, Typography, FormControlLabel, Switch } from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CustomButton from '../../../components/Button';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Form, InjectedFormProps, reduxForm } from 'redux-form';
import _difference from 'lodash/difference';
import { useTypedSelector } from '../../../reducers';
import { getUser, resetUser, TFF_ACCESS_ROLE, updateUserRole } from '../../../reducers/Users/usersSlice';
import {
  MarketsByRegionType,
  ProductConfigType,
  Role,
  SourceConfigType,
  SystemConfigType,
} from '../../../types/administrationTypes';
import MarketsConfiguration from './MarketsConfiguration';
import ProductsConfiguration from './ProductsConfiguration';
import SourcesConfiguration from './SourcesConfiguration';
import SystemsConfiguration from './SystemsConfiguration';
import UserDataConfiguration from './UserDataConfiguration';
import { defaultMarketsByRegion, defaultProducts, defaultSources, defaultSystems } from './values';

interface EditUserFormProps {
  onUserFormChange: (userUpdated: boolean) => void;
  mainRoles: Role[];
  userId?: string;
}

const EditUserForm: React.FC<EditUserFormProps & InjectedFormProps<{}, EditUserFormProps, string>> = ({
  change,
  handleSubmit,
  initialize,
  onUserFormChange,
  mainRoles,
  userId,
}) => {
  const { roles } = useTypedSelector(({ rolesData }) => rolesData);
  const { userForm } = useTypedSelector(({ form }) => form);
  const { user } = useTypedSelector(({ usersData }) => usersData);

  const formValues = userForm?.values;

  const [userRoles, setUserRoles] = useState<Role[]>();
  const [userMainRole, setUserMainRole] = useState<Role>();
  const [addedMarketRoles, setAddedMarketRoles] = useState<Role[]>([]);
  const [addedProductRoles, setAddedProductRoles] = useState<Role[]>([]);
  const [removedMarketRoles, setRemovedMarketRoles] = useState<Role[]>([]);
  const [removedProductRoles, setRemovedProductRoles] = useState<Role[]>([]);
  const [addedSourcesRoles, setAddedSourcesRoles] = useState<Role[]>([]);
  const [removedSourcesRoles, setRemovedSourcesRoles] = useState<Role[]>([]);
  const [addedSystemRoles, setAddedSystemRoles] = useState<Role[]>([]);
  const [removedSystemRoles, setRemovedSystemRoles] = useState<Role[]>([]);
  const [clientAccess, setClientAccess] = useState<boolean>(false);
  const [userSelectedMainRole, setUserSelectedMainRole] = useState<string>('');

  const intl = useIntl();
  const dispatch = useDispatch();

  useEffect(() => {
    if (userId) {
      dispatch(getUser(userId));
    }
  }, [userId]);

  useEffect(() => {
    if (user) {
      initialize(user);
    }
  }, [user]);

  const [productsList, setProductsList] = useState<ProductConfigType[]>(defaultProducts);
  const [marketsState, setMarketsState] = useState<MarketsByRegionType>(defaultMarketsByRegion);
  const [sourcesList, setSourcesList] = useState<SourceConfigType[]>(defaultSources);
  const [systemsList, setSystemLists] = useState<SystemConfigType[]>(defaultSystems);

  useEffect(() => {
    //update products when market changes
    const selectedMarkets: string[] = [];

    Object.values(marketsState).forEach(regionMarkets => {
      regionMarkets.forEach(market => {
        if (market.selected) {
          selectedMarkets.push(market.value);
        }
      });
    });

    // Update products based on selected markets
    const newProductsState = productsList.map(item => {
      const shouldEnable = selectedMarkets.some(marketValue => item.belongsTo.includes(marketValue));
      return shouldEnable ? { ...item, disabled: false } : item;
    });
    setProductsList(newProductsState);

    const changedMarkets = selectedMarkets;

    const marketRoles = roles.filter((role: Role) => role.name.startsWith('M_'));

    const userMarketRoles = formValues?.roles
      ?.filter((role: Role) => role.name.startsWith('M_'))
      ?.map((role: Role) => role.name);

    const rolesToAddDiff = _difference(changedMarkets, userMarketRoles);
    const rolesToRemoveDiff = _difference(userMarketRoles, changedMarkets);
    const rolesToAdd: Role[] = [];
    marketRoles.forEach(role => {
      rolesToAddDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToAdd.push(role);
        }
      });
    });
    const rolesToRemove: Role[] = [];
    marketRoles.forEach(role => {
      rolesToRemoveDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToRemove.push(role);
        }
      });
    });
    setAddedMarketRoles(rolesToAdd);
    setRemovedMarketRoles(rolesToRemove);
  }, [marketsState]);

  useEffect(() => {
    const changedProducts = productsList.filter(product => product.selected).map(product => product.value);
    const productRoles = roles.filter((role: Role) => role.name.startsWith('P_'));

    const userProductRoles = formValues?.roles
      ?.filter((role: Role) => role.name.startsWith('P_'))
      ?.map((role: Role) => role.name);

    const rolesToAddDiff = _difference(changedProducts, userProductRoles);
    const rolesToRemoveDiff = _difference(userProductRoles, changedProducts);
    const rolesToAdd: Role[] = [];
    productRoles.forEach(role => {
      rolesToAddDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToAdd.push(role);
        }
      });
    });
    const rolesToRemove: Role[] = [];
    productRoles.forEach(role => {
      rolesToRemoveDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToRemove.push(role);
        }
      });
    });
    setAddedProductRoles(rolesToAdd);
    setRemovedProductRoles(rolesToRemove);
  }, [productsList]);

  useEffect(() => {
    const changedSources = sourcesList.filter(source => source.selected).map(source => source.value);
    const sourcesRoles = roles.filter((role: Role) => role.name.startsWith('S_'));

    const userSourcesRoles = formValues?.roles
      ?.filter((role: Role) => role.name.startsWith('S_'))
      ?.map((role: Role) => role.name);

    const rolesToAddDiff = _difference(changedSources, userSourcesRoles);
    const rolesToRemoveDiff = _difference(userSourcesRoles, changedSources);
    const rolesToAdd: Role[] = [];
    sourcesRoles.forEach(role => {
      rolesToAddDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToAdd.push(role);
        }
      });
    });
    const rolesToRemove: Role[] = [];
    sourcesRoles.forEach(role => {
      rolesToRemoveDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToRemove.push(role);
        }
      });
    });
    setAddedSourcesRoles(rolesToAdd);
    setRemovedSourcesRoles(rolesToRemove);
  }, [sourcesList]);

  useEffect(() => {
    const changedSystems = systemsList.filter(source => source.selected).map(source => source.value);
    const systemRoles = roles.filter((role: Role) => role.name.startsWith('SY_'));

    const userSystemRoles = formValues?.roles
      ?.filter((role: Role) => role.name.startsWith('SY_'))
      ?.map((role: Role) => role.name);

    const rolesToAddDiff = _difference(changedSystems, userSystemRoles);
    const rolesToRemoveDiff = _difference(userSystemRoles, changedSystems);
    const rolesToAdd: Role[] = [];
    systemRoles.forEach(role => {
      rolesToAddDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToAdd.push(role);
        }
      });
    });
    const rolesToRemove: Role[] = [];
    systemRoles.forEach(role => {
      rolesToRemoveDiff.forEach(diff => {
        if (role.name === diff) {
          rolesToRemove.push(role);
        }
      });
    });
    setAddedSystemRoles(rolesToAdd);
    setRemovedSystemRoles(rolesToRemove);
  }, [systemsList]);

  useEffect(() => {
    const rolesToAdd = addedMarketRoles.concat(...addedProductRoles);
    const rolesToAddWithSources = rolesToAdd.concat(...addedSourcesRoles);
    const rolesToAddWithSystems = rolesToAddWithSources.concat(...addedSystemRoles);

    if (userMainRole) {
      const newRoles = [...rolesToAddWithSystems, userMainRole];
      change('rolesToAdd', newRoles);
    } else {
      change('rolesToAdd', rolesToAddWithSystems);
    }
  }, [addedMarketRoles, addedProductRoles, addedSourcesRoles, addedSystemRoles, change, userMainRole]);

  useEffect(() => {
    const rolesToRemove = removedMarketRoles.concat(...removedProductRoles);
    const rolesToRemoveWithSources = rolesToRemove.concat(...removedSourcesRoles);
    const rolesToRemoveWithSystems = rolesToRemoveWithSources.concat(...removedSystemRoles);
    if (userMainRole) {
      const userMainRoleToRemove = userRoles?.find(role => role.name.startsWith('Role_'));
      if (userMainRoleToRemove) {
        if (userMainRoleToRemove.name !== userMainRole.name) {
          const newRoles = [...rolesToRemoveWithSystems, userMainRoleToRemove];
          change('rolesToRemove', newRoles);
        }
      }
    } else {
      change('rolesToRemove', rolesToRemoveWithSystems);
    }
  }, [
    removedMarketRoles,
    removedProductRoles,
    removedSourcesRoles,
    removedSystemRoles,
    change,
    userMainRole,
    userRoles,
  ]);

  useEffect(() => {
    if (!formValues?.roles) return;

    setUserRoles(formValues.roles);

    const updateSelectedState = (list, prefix) => {
      const finalState = [...list];
      const matchedRoles = findRoles(formValues.roles, prefix);
      if (matchedRoles) {
        matchedRoles.forEach(role => {
          const index = finalState.findIndex(item => item.value === role.name);
          if (index !== -1) {
            finalState[index] = { ...finalState[index], selected: true };
          }
        });
      }
      return finalState;
    };

    // Update markets state
    const matchedMarketRoles = findRoles(formValues.roles, 'M_');
    if (matchedMarketRoles) {
      const updatedMarketsState = { ...marketsState };
      Object.keys(updatedMarketsState).forEach(regionKey => {
        updatedMarketsState[regionKey] = updatedMarketsState[regionKey].map(market => {
          const isSelected = matchedMarketRoles.some(role => role.name === market.value);
          return { ...market, selected: isSelected };
        });
      });
      setMarketsState(updatedMarketsState);
    }

    // Update products state
    const filteredProductsList = productsList.map(item => ({
      ...item,
      disabled: !item.belongsTo.some(belongsToName => formValues.roles.includes(`M_${belongsToName}`)),
    }));
    const updatedProductsState = updateSelectedState(filteredProductsList, 'P_');
    setProductsList(updatedProductsState);

    // Update sources state
    const updatedSourcesState = updateSelectedState(sourcesList, 'S_');
    setSourcesList(updatedSourcesState);

    // Update systems state
    const updatedSystemsState = updateSelectedState(systemsList, 'SY_');
    setSystemLists(updatedSystemsState);
  }, [formValues?.roles, formValues?.accessRole]);

  useEffect(() => {
    if (clientAccess) {
      change('accessRole', [TFF_ACCESS_ROLE.name]);
    } else {
      change('accessRole', {});
    }
  }, [clientAccess]);

  useEffect(() => {
    if (userRoles) {
      const mainRole = mainRoles.find(
        role => role.name === userRoles?.find(userRole => userRole?.name.startsWith('Role_'))?.name,
      );
      setUserSelectedMainRole(mainRole?.id ?? '');
    }
  }, [userRoles, change, mainRoles]);

  const findRoles = (roles: Role[], substring: string) => {
    return roles.filter(role => role.name.startsWith(substring));
  };

  const onSave = (values: any) => {
    if (values) {
      dispatch(updateUserRole(values));
      onUserFormChange(true);
      dispatch(resetUser());
    }
  };

  const onCancel = () => {
    onUserFormChange(false);
    dispatch(resetUser());
  };

  const handleMainRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value;
    const role = mainRoles.find(role => role.id === id);
    setUserSelectedMainRole(id);
    setUserMainRole(role);
  };

  const handleClientAccess = (event: React.ChangeEvent<HTMLInputElement>) => {
    setClientAccess(event.target.checked);
  };

  const activeInactiveTitle = () => {
    return clientAccess
      ? intl.formatMessage({ id: 'administration.user_activated' })
      : intl.formatMessage({ id: 'administration.user_deactivated' });
  };

  return (
    <div>
      <Form className="tff-administration-add-employee-wrapper" onSubmit={handleSubmit(onSave)}>
        <div className="tff-administration-row">
          <div>
            <Button className="tff-administration-flex-row tff-administration-button-no-transform" onClick={onCancel}>
              <div className="tff-administration-back-icon-wrapper">
                <ArrowBackIcon color="primary" />
              </div>
              <Typography color="primary" variant="h6" className="tff-administration-typography-margin">
                {intl.formatMessage({ id: 'administration.editUser' })}
              </Typography>
            </Button>
          </div>
          <div className="tff-administration-user-deactivate">
            <FormControlLabel
              control={<Switch checked={clientAccess} onChange={handleClientAccess} name="Active" />}
              label={activeInactiveTitle()}
            />
          </div>
        </div>
        <UserDataConfiguration
          userSelectedMainRole={userSelectedMainRole}
          mainRoles={mainRoles}
          handleMainRoleChange={handleMainRoleChange}
        />
        <div className="tff-administration-row tff-administration-row-margin">
          <MarketsConfiguration
            marketsState={marketsState}
            setMarketsState={setMarketsState}
            productsList={productsList}
            setProductsList={setProductsList}
          />
          <ProductsConfiguration productsList={productsList} setProductsList={setProductsList} />
          <div className="tff-administration-col-flex tff-administration-col-4">
            <SourcesConfiguration sourcesList={sourcesList} setSourcesList={setSourcesList} />
            <SystemsConfiguration systemsList={systemsList} setSystemsList={setSystemLists} />
          </div>
        </div>

        <div className="tff-administration-flex-end-center">
          <div className="tff-administration-button-margin">
            <CustomButton variant="contained" color="primary" size="medium" onClick={onCancel}>
              {`${intl.formatMessage({ id: 'administration.cancel' })}`}
            </CustomButton>
          </div>
          <CustomButton variant="contained" color="danger" size="medium" type="submit">
            {`${intl.formatMessage({ id: 'administration.saveSettings' })}`}
          </CustomButton>
        </div>
      </Form>
    </div>
  );
};

export default reduxForm<{}, EditUserFormProps>({
  form: 'userForm',
})(EditUserForm);
